
<template>
  <div class="satd-saisies-list-page">

    <!-- SUBMENU -->
    <div class="satd-saisies-list-page__submenu">

      <!-- FILTERS -->
      <b-card
        class="satd-saisies-list-page__filters"
        body-class="d-flex justify-content-space-between align-items-flex-start gap-10 p-1"
      >
        <!-- Filters: first column -->
        <satd-saisie-list-filters
          :class="[
            isConsumerPending ? 'satd-saisies-list-page__filters--busy' : '',
            'd-flex flex-grow-1 flex-wrap flex-basis-0'
          ]"
          v-bind="{
            filters: context.filters,
            updateFiltersCallback: setFilters,
            loadSalarieOptionsCallback: loadSalarieOptions,
            resetFiltersCallback: resetFilters,
          }"
        />

        <!-- Filters: second column -->
        <ui-wad class="d-flex flex-shrink-0">
          <ui-tooltip-wrapper
            class="d-inline-block"
            v-bind="{
              title: `Vous n'avez pas les droits pour effectuer cette action`,
              variant: UI.VARIANT.LIGHT,
            }"
            :status="hasACL([
                ACLS.SATD_CREATE,
              ]) ?
              UI.STATUS.DISABLED :
              UI.STATUS.ENABLED
            "
          >
            <layout-cta-sub
              component="ui-link-button"
              :variant="UI.VARIANT.PRIMARY"
              :route="creationRoute"
              :size="UI.SIZE.SMALL"
              :status="hasACL(ACLS.SATD_CREATE) ?
                UI.STATUS.ENABLED :
                UI.STATUS.DISABLED
              "
            >
              <ui-icon name="add" />
              &nbsp;Ajouter une saisie
            </layout-cta-sub>
          </ui-tooltip-wrapper>
        </ui-wad>

      </b-card>
    </div>

    <div class="satd-saisies-list-page__main">

      <!-- TABLE -->
      <div class="satd-saisies-list-page__table-scroll">
        <ui-table-wrapper
          ref="table"
          class="satd-saisies-list-page__table-main"
          :provider="provider"
          :fields="fields"
          :sort-by="context.order"
          :sort-desc="context.sort === 'desc'"
          primary-key="saisie_id"
          :tbody-tr-class="tbodyRowClass"
          :size="UI.SIZE.LARGE"
          :hide-header="providerCallCount === 0"
          hover
          striped
          fixed
          @row-clicked="({ item }) => openSaisiePanel(item)"
          @row-dblclicked="({ item }) => editSaisie(item)"
        >

          <template
            v-if="providerCallCount === 0"
            #table-busy
          >
            <layout-placeholder-table-body
              :count="context.perPage"
              :size="UI.SIZE.SMALL"
              striped
              class="satd-saisies-list-page__placeholder"
            />
          </template>

          <template #empty>
            <div class="satd-saisies-list-page__empty">
              <ui-icon name="search_off" class="font-size-40px color-black" />
              <span class="font-weight-bold color-black">Aucun résultat trouvé</span>
              <span>
                Nous n'avons trouvé aucune saisie correspondant à votre recherche.
              </span>
              <layout-cta-sub
                v-if="hasAnyCriterionSet"
                component="ui-button"
                :variant="UI.VARIANT.LIGHT"
                :size="UI.SIZE.SMALL"
                @click="resetFilters"
              >
                Réinitialiser les filtres
              </layout-cta-sub>
            </div>
          </template>

          <template
            v-if="
              (
                isHolisticSuggestionDisplayed ||
                isHolisticSelectionEnabled
              )
            "
            #top-row="{ columns }"
          >
            <ui-table-top-row-selection
              :suggestion-displayed="isHolisticSuggestionDisplayed"
              :selection-displayed="isHolisticSelectionEnabled"
              :selected-count="bufferedSelectedItemsCount"
              :total-count="context.total"
              :colspan="columns"
              class="satd-saisies-list-page__row-selection"
              @unselect="resetSelection"
              @select="setHolisticSelection"
            />
          </template>

          <template #head(selection)>
            <ui-tooltip-wrapper
              :status="context.filters.periode
                ? UI.STATUS.DISABLED
                : UI.STATUS.ENABLED
              "
              title="Renseignez une période"
              placement="top-start"
              no-animation
              :offset="6"
              variant="primary"
            >
              <ui-button-icon
                :status="
                  providerCallCount === 0 || !context.filters.periode
                    ? UI.STATUS.DISABLED
                    : UI.STATUS.ENABLED
                "
                :size="UI.SIZE.SMALL"
                :overlay-size="UI.SIZE.LARGE"
                :class="[
                  checkboxAllIconClassname,
                  { 'satd-saisies-list-page--checkbox-disabled': !context.filters.periode },
                ]"
                :name="checkboxAllIconName"
                :theme="UI.ICON_THEME.FILLED"
                @click="toggleSelectAll"
              />
            </ui-tooltip-wrapper>
          </template>

          <template #cell(selection)="data">

          <ui-tooltip-wrapper
            :status="context.filters.periode
              ? UI.STATUS.DISABLED
              : UI.STATUS.ENABLED
            "
            title="Renseignez une période"
            class="d-inline-block min-w-0"
            placement="top-start"
            no-animation
            :offset="6"
            variant="primary"
            >
            <ui-button-icon
              :size="UI.SIZE.SMALL"
              :status="!context.filters.periode
                ? UI.STATUS.DISABLED
                : UI.STATUS.ENABLED
              "
              :overlay-size="UI.SIZE.LARGE"
              :variant="UI.VARIANT.DARK"
              :name="isItemSelected(data.item) ?
                'check_box' :
                'check_box_outline_blank'
              "
              :theme="UI.ICON_THEME.FILLED"
              :class="[
                isItemSelected(data.item) ?
                  'color-primary' :
                  'color-gray-dark',
                  { 'satd-saisies-list-page--checkbox-disabled': !context.filters.periode },
              ]"
              @click="toggleItem(data.item)"
            />
            </ui-tooltip-wrapper>
          </template>

          <template #cell(salarie)="props">
            <ui-tooltip-wrapper
              v-if="props.item.salarie"
              :title="salarieTooltip(props.item.salarie)"
              class="d-block min-w-0"
              placement="top-start"
              no-caret
              no-animation
              :offset="6"
              :delay="100"
            >
              <span class="d-block text-truncate">
                {{ props.item.salarie.nom }} {{ props.item.salarie.prenom }}
              </span>
            </ui-tooltip-wrapper>
          </template>

          <template
            v-if="!context.filters.periode"
            #head(montant_passe)="props"
          >
            <ui-tooltip-wrapper
              class="satd-saisies-list-page__header-tooltip"
              v-bind="{
                title: `Cumul des retenues déjà passées en paie sur cette saisie, depuis l'origine et jusqu'à aujourd'hui. Sélectionnez une période pour n'afficher que les retenues de cette période.`,
                tooltipClass: 'font-size-80',
                placement: 'top',
              }"
            >
              <ui-icon
                name="info"
                class="satd-saisies-list-page__header-tooltip-icon"
              />
              <span>{{ props.label }}</span>
            </ui-tooltip-wrapper>
          </template>

          <template
            v-if="!context.filters.periode"
            #head(solde_du)="props"
          >
            <ui-tooltip-wrapper
              class="satd-saisies-list-page__header-tooltip"
              v-bind="{
                title: `Montant total de la saisie moins les retenues déjà passées en paie : ce qu'il reste à recouvrer aujourd'hui.`,
                tooltipClass: 'font-size-80',
                placement: 'top',
              }"
            >
              <ui-icon
                name="info"
                class="satd-saisies-list-page__header-tooltip-icon"
              />
              <span>{{ props.label }}</span>
            </ui-tooltip-wrapper>
          </template>

          <template #cell(integre_paie)="data">
            <ui-popover-wrapper
              v-if="data.item.mois_preleve?.length"
              no-caret
              popover-class="satd-saisies-list-page__popover"
            >
              <ui-icon
                class="satd-saisies-list-page__integre-paie-icon"
                name="check"
              />

              <template #popover>
                <div>
                  <ui-wad
                    v-for="mois in data.item.mois_preleve"
                    :key="mois"
                  >
                    &bull; {{ mois }}
                  </ui-wad>
                </div>
              </template>
            </ui-popover-wrapper>
          </template>

          <template #cell(actions)="data">
            <ui-wad class="satd-saisies-list-page__actions">
              <ui-dropdown-wrapper
                :menu-variant="UI.VARIANT.PRIMARY"
                data-cy="action_menu"
                placement="right-start"
              >
                <template #button-content>
                  <ui-button-icon
                    name="more_vert"
                    :variant="UI.VARIANT.DARK"
                    :size="UI.SIZE.SMALL"
                    :overlay-size="UI.SIZE.MEDIUM"
                    square
                    ripple
                    data-cy="action_button"
                  />
                </template>

                <template #default>
                  <b-dropdown-item
                    :disabled="!hasACL(ACLS.SATD_UPDATE)"
                    :to="getSaisiesEditRoute(data.item.saisie_id)"
                    data-cy="edit_saisie_button"
                  >
                    <ui-icon name="file_open" />
                    Modifier la saisie
                  </b-dropdown-item>
                </template>
              </ui-dropdown-wrapper>
            </ui-wad>
          </template>

          <template
            v-if="isCumulVisible && context.filters.periode"
            #custom-foot="{ fields: footFields }"
          >
            <tr
              :class="[
                'satd-saisies-list-page__cumul-row',
                { 'satd-saisies-list-page__cumul-row--loading': isCumulsLoading },
              ]"
            >
              <!-- @XXX "bootstrap-vue-next" sorting feature also work on tfooter
                ↳ workaround: use td instead of th -->
              <td
                v-for="field in footFields"
                :key="field.key"
                :class="field.class"
              >
                <template v-if="field.key === 'selection'">
                  <ui-wad class="d-flex align-items-center gap-1">
                    <span class="satd-saisies-list-page__cumul-row__label">Cumul</span>
                    <ui-buffered-pending-icon
                      :is-pending="isCumulsLoading"
                      :idle-phase-duration="300"
                      :sustain-phase-duration="300"
                      :size="UI.SIZE.SMALL"
                      :type="UI.SPINNER_TYPE.BORDER"
                    />
                  </ui-wad>
                </template>
                <template v-if="field.key === 'montant_periode'">
                  {{ formatCumul('montant_periode_total') }}
                </template>
              </td>
            </tr>
          </template>
        </ui-table-wrapper>
      </div>

      <!-- FOOTER -->
      <div class="satd-saisies-list-page__footer">

        <span
          v-hide="context.total === 0"
          class="satd-saisies-list-page__footer__count"
        >
          <layout-misc-float :value="context.total" />
          {{ context.total <= 1 ? 'résultat' : 'résultats' }}
        </span>

        <ui-blank
          v-if="context.total === 0"
          class="satd-saisies-list-page__footer__blank"
        />

        <ui-table-pagination
          v-model="context.currentPage"
          v-hide="context.total < context.perPage || context.total === 0"
          :total-rows="context.total"
          :per-page="context.perPage"
        />

        <div class="satd-saisies-list-page__footer__per-page">
          <ui-form-select
            v-model="context.perPage"
            :size="UI.SIZE.SMALL"
            :options="perPageOptions"
            class="satd-saisies-list-page__footer__per-page__select"
          />
          <div class="satd-saisies-list-page__footer__per-page__text">
            par page
          </div>
        </div>

      </div>
    </div>

    <!-- CANDY BAR: action menu -->
    <ui-candy-bar
      class="satd-saisies-list-page__candy-bar"
      :model-value="hasAnyItemSelected"
      :aside-counter="{
        value: selectionCount,
        singular: 'sélectionnée',
        plural: 'sélectionnées',
      }"
      fit-workspace
      :offset="105"
      @clear="resetSelection"
    >
      <ui-popover-wrapper
        popover-class="satd-saisies-list-page__popover"
        hover
        :delay="{
          show: 0,
          hide: 2000
        }"
      >
        <ui-button
          :variant="UI.VARIANT.PRIMARY"
          :size="UI.SIZE.SMALL"
          :status="hasACL(ACLS.SATD_CREATE)
            ? UI.STATUS.ENABLED
            : UI.STATUS.DISABLED"
          outline
        >
          <ui-icon name="account_balance" />
          Générer fichier de virement
        </ui-button>
        <template #popover>
          <div v-if="hasACL(ACLS.SATD_CREATE)">
            <ui-wad class="font-weight-bold pb-1">
              Certaines saisies ont peut-être déjà été reversées.
            </ui-wad>
            <ui-wad class="d-flex gap-1">
              En continuant, elles seront à nouveau incluses dans le fichier à la date d'aujourd'hui.
              <ui-button
                :size="UI.SIZE.MEDIUM"
                @click="generateFiles"
              >
                <ui-icon
                  name="check"
                />
              </ui-button>
            </ui-wad>
          </div>
        </template>
      </ui-popover-wrapper>
    </ui-candy-bar>

    <ui-sidebar
      :id="SATD_SAISIE_SIDEBAR_ID"
      v-model="displaySaisieSidebar"
      :size="UI.SIZE.SMALL"
      class="satd-saisies-list-page__sidebar"
      body-class="p-0"
      placement="end"
      shadow
      lazy
      teleport-disabled
      no-header
      fit-workspace
    >
      <saisie-summary-organism
        v-if="displaySaisieSidebar && currentSaisie"
        :saisie-id="currentSaisie.saisie_id"
        @close="displaySaisieSidebar = false"
      />
    </ui-sidebar>
  </div>
</template>

<script lang="ts">
import { useTableProvider, type DefaultData as ProviderDefaultData } from '~/composables/layout/table-provider'
import {  useAccessControl } from '~/composables/layout/access-control';
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation';
import { getSaisiesCreationRoute, getSaisiesDetailsRoute } from '~/stores/satd';
import { getDefaultFilters, useStore_satd_saisies_list } from '~/stores/satd/saisies/list';
import type { SaisieCumuls, SaisieWithSalarie, SatdSaisieListFilters } from '~/stores/satd/saisies/list';
import { useStore_satd_saisies_list_salarie, type SaisieSalarie } from '~/stores/satd/saisies/list/salarie';
import type { UiTableWrapper } from '#components';
import type { ComponentExposed } from 'vue-component-type-helpers'
import { any, equals, map, Maybe, notEmpty, omit, or, prop, values } from '~/utils/lib/core/prelude';
import { parseDatetime } from '~/utils/lib/core/datetime';
import { launchWindowDownload } from '~/utils/lib/core/download';
import { NAVIGATION_SCOPE } from '~/utils/lib/layout/types';
import { toString } from '~/utils/lib/core/float';
import { wrap as wrapInput } from '~/utils/infra/common/types';
import SaisieSummaryOrganism from '~/components/satd/saisies/summary/Organism.vue';
import SatdSaisieListFiltersComponent from '~/components/satd/saisies/list/Filters.vue';
import type { TableField } from '~/components/ui/table/Wrapper.vue'
import { useTableSelectionMultipleHolistic } from '~/composables/layout/table-selection-multiple-holistic';
import { useToastBuilder } from '~/composables/layout/toast';
import Console from '~/utils/lib/core/console'
import { useIncrementalToken } from '~/composables/core/incremental-token';

const SATD_SAISIE_SIDEBAR_ID = 'satd-saisie-list-inspection-sidebar'
const console = new Console('page:satd-list')

function formatMontant(value: number): string {
  return toString(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
}

function parseDate(date: string): string {
  return parseDatetime(
    { inputFormat: undefined, outputFormat: 'DD/MM/YYYY' },
    date
  )
}

export default defineNuxtComponent({
  components: {
    SaisieSummaryOrganism,
    SatdSaisieListFilters: SatdSaisieListFiltersComponent,
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
    })
    const navigationContext = useCompanyRequiredNavigationContext()
    const ref_table = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')
    const store_saisies_list = useStore_satd_saisies_list()
    const store_salarie = useStore_satd_saisies_list_salarie()
    const ToastBuilder = useToastBuilder()

    const providerDefaultData :ProviderDefaultData = {
      context: {
        perPage: 50,
        order: 'created_at',
        sort: 'desc',
        filters: getDefaultFilters()
      }
    }

    const tableProvider = useTableProvider<SaisieWithSalarie, SatdSaisieListFilters>(
      ref_table,
      providerDefaultData
    )

    const tableSelectionMultiple = useTableSelectionMultipleHolistic(
      tableProvider.currentItems,
      (item: SaisieWithSalarie, item_: SaisieWithSalarie) => {
        const id  = prop('saisie_id', item)
        const id_ = prop('saisie_id', item_)
        return equals(id, id_)
      },
      tableProvider.context,
    )

    const hasAnyCriterionSet = computed(() => {
      const filters_ = omit([ 'periode' ], tableProvider.context.value.filters ?? {})

      return or(
        any(notEmpty, values(filters_ as unknown as object)),
        notEmpty(tableProvider.context.value.search)
      )
    })

    return {
      ...navigationContext,
      ...useAccessControl(),
      ...tableProvider,
      hasAnyCriterionSet,
      ...tableSelectionMultiple,
      store_saisies_list,
      store_salarie,
      ToastBuilder,
      cumulsFetchToken: useIncrementalToken(),
    }
  },
  data() {
    const {
      structure: { id },
      company: { code_societe }
      // @ts-expect-error: "data" hook not injecting "setup" dependencies
    } = this.fromCompanyLevel()

    const creationRoute = getSaisiesCreationRoute(id, code_societe)

    return {
      SATD_SAISIE_SIDEBAR_ID,
      displaySaisieSidebar: false,
      currentSaisie: null as null | SaisieWithSalarie,
      creationRoute,
      cumuls: Maybe.Nothing<SaisieCumuls>(),
      isCumulsLoading: false,
      perPageOptions: [
        { value: 50, text: '50' },
        { value: 100, text: '100' },
        { value: 200, text: '200' },
      ],
    }
  },
  computed: {
    fields(): TableField[] {
      const periodActivated = this.context.filters.periode
        ? true
        : false

      return [
        {
          key: 'selection',
          label: '',
          class: 'satd-saisies-list-page__selection',
        },
        {
          key: 'salarie',
          label: 'Salarié',
          class: 'satd-saisies-list-page__salarie',
        },
        {
          key: 'type_saisie',
          label: 'Type',
          class: 'satd-saisies-list-page__type-saisie',
        },
        {
          key: 'date_avis',
          label: "Date de l'avis",
          class: 'satd-saisies-list-page__date-avis',
          formatter: ({ value }) => parseDate(value as string)
        },
        {
          key: 'montant_total',
          label: 'Montant total',
          class: 'satd-saisies-list-page__montant-total',
          formatter: ({ value }) => formatMontant(value as number),
        },
        {
          key: 'montant_passe',
          label: 'Saisies déjà passées',
          class: [
            'satd-saisies-list-page__montant-passe',
            // { 'satd-saisies-list-page__montant-passe--disable': !periodActivated }
          ],
          formatter: ({ value }) =>
            formatMontant(value as number)
            // : " — ",
        },
        {
          key: 'montant_periode',
          label: 'Saisies de la période',
          class: [
            'satd-saisies-list-page__montant-periode',
            { 'satd-saisies-list-page__montant-periode--disable': !periodActivated }
          ],
          formatter: ({ value }) => periodActivated
            ? formatMontant(value as number)
            : " — "
        },
        {
          key: 'solde_du',
          label: 'Solde dû',
          class: 'satd-saisies-list-page__solde-du',
          formatter: ({ value }) => formatMontant(value as number),
        },
        {
          key: 'integre_paie',
          label: 'Intégré en paie',
          class: 'satd-saisies-list-page__integre-paie',
        },
        {
          key: 'actions',
          label: '',
          class: 'satd-saisies-list-page__actions',
        },
      ] as TableField[]
    },
    selectionCount(): number {
      return this.selectionType === 'all'
        ? this.context.total
        : this.bufferedSelectedItemsCount
    },
    isCumulVisible(): boolean {
      return this.hasAnyItemSelected
    },
  },
  watch: {
    'context.filters'() {
      this.resetSelection()
    },
    'context.currentPage'() {
      this.resetSelection()
    },
  },
  created() {
    this.consumer = (context) => {
      const {
        structure: { uuidData },
        company: { societeId }
      } = this.fromCompanyLevel()

      return this.store_saisies_list.list({
        uuidData,
        societeId,
        input: wrapInput(context),
      })
    }
    this.resetFilters()
  },
  mounted() {
    this.$watch(
      instance => [
        instance.selectionType,
        instance.bufferedSelectedItems
      ],
      () => this.refreshCumuls()
    )
  },
  methods: {
    loadSalarieOptions(search: string) {
      const {
        structure: { uuidData },
        company: { societeId }
      } = this.fromCompanyLevel()

      return this.store_salarie.search({ uuidData, societeId, search })
    },
    formatCumul(key: keyof SaisieCumuls): string {
      return Maybe.maybe(
        '',
        (cumuls: SaisieCumuls) => formatMontant(cumuls[key]),
        this.cumuls
      )
    },
    refreshCumuls() {
      switch (this.hasAnyItemSelected) {

        case true:
          this.fetchCumuls()
          break

        case false:
          this.cumulsFetchToken.inc()
          this.cumuls = Maybe.Nothing()
          this.isCumulsLoading = false
          break

      }
    },
    fetchCumuls() {
      const {
        structure: { uuidData },
        company: { societeId }
      } = this.fromCompanyLevel()

      const select_all = this.selectionType === 'all'
      const saisies_ids = select_all
        ? []
        : map.iterable(
          prop('saisie_id'),
          this.bufferedSelectedItems,
        )

      this.cumulsFetchToken.inc()
      const token = this.cumulsFetchToken.current
      this.isCumulsLoading = true

      return this.store_saisies_list.cumuls({
        uuidData,
        societeId,
        select_all,
        saisies_ids,
        filters: this.context.filters,
      })
        .then((montant_periode_total) => {
          if (token !== this.cumulsFetchToken.current)
            return

          this.cumuls = Maybe.Just(montant_periode_total)
        })
        .catch((err) => {
          if (token !== this.cumulsFetchToken.current)
            return

          console.warn('fetchCumuls', err)
          this.ToastBuilder
            .error(String(err))
            .title('Erreur lors du calcul des cumuls')
            .show()
        })
        .finally(() => {
          if (token !== this.cumulsFetchToken.current)
            return

          this.isCumulsLoading = false
        })
    },
    editSaisie(item: SaisieWithSalarie) {
      if (!this.hasACL(this.ACLS.SATD_UPDATE)) return
      return navigateTo(this.getSaisiesEditRoute(item.saisie_id))
    },
    getSaisiesEditRoute(saisie_id: string): string {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()

      return getSaisiesDetailsRoute(id, code_societe, saisie_id)
    },
    openSaisiePanel(item: SaisieWithSalarie) {
      this.currentSaisie = item
      this.displaySaisieSidebar = true
    },
    resetFilters() {
      this.store_salarie.$patch({ selectedSalaries: []})
      this.resetCriterion()
    },
    salarieTooltip(salarie: SaisieSalarie): string {
      if (salarie.matricule)
        return `${salarie.nom} ${salarie.prenom} — ${salarie.matricule}`

      return `${salarie.nom} ${salarie.prenom}`
    },
    generateFiles() {
      const {
        structure: { uuidData },
        company: { societeId, raison_sociale }
      } = this.fromCompanyLevel()

      const select_all = this.selectionType === 'all'
      const saisies_ids = select_all
        ? []
        : map.iterable(
          prop('saisie_id'),
          this.bufferedSelectedItems,
        )

      return this.store_saisies_list.generateXml({
        uuidData,
        societeId,
        raisonSociale: raison_sociale,
        select_all,
        saisies_ids,
        filters: this.context.filters,
      })
        .then(({ xml, xlsx, warning }) => {
          if (!xml) {
            return  this.ToastBuilder
            .warning(warning)
            .title("Aucun fichier généré")
            .show()
          }

          // Warning
          // generateFilename produces a single name with no extension, and both the XML and XLSX blobs reuse it.
          // Sequential launchWindowDownload calls then save two different files under the same download name,
          // so one can overwrite the other and neither is identifiable as XML or spreadsheet.
          launchWindowDownload(xml.data, xml.filename)
          launchWindowDownload(xlsx!.data, xlsx!.filename)

          const title = "Fichier de virement généré" + (warning ? " — saisies sans prélèvements exclues" : "")
          const content = "Téléchargement en cours..." + (warning ? `\n${warning}` : "")

          this.ToastBuilder
            .success(content)
            .class('toast-pre-line')
            .title(title)
            .show()
        })
        .catch((err) => {
          console.warn('generateFiles', err)
          this.ToastBuilder
            .error(String(err))
            .title('Erreur lors de la génération du fichier de virement')
            .show()
        })
    },
  },
})

</script>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/table/members'
@import '~/assets/sass/abstracts/members'
@import '~/assets/sass/modules/client/members'

$actions-column-width: 34px
$selection-column-width: 50px
$date-avis-column-width: 110px
$salarie-column-width: 210px
$type-saisie-column-width: 140px
$integre-paie-column-width: 120px
$montant-column-width: 140px
$cumul-row-height: 37px
$placeholder-grace-delay: 300ms

.satd-saisies-list-page
  $self: &

  height: 100%
  display: flex
  flex-direction: column

  &__submenu
    padding: ($layout-spacer-y * .5) ($layout-spacer-x * .75) ($layout-spacer-y * .5)
    background-color: $background-primary

  &__main
    height: 100%
    display: flex
    flex-direction: column

  &__table-scroll
    @include always-show-scrollbar()
    contain: size
    overflow-x: scroll
    height: 100%
    flex-grow: 1
    scroll-padding-bottom: $cumul-row-height
    container-type: inline-size

  //////////////////////////////////////

  &__filters-container

    &--busy
      @include no-interaction
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

  //////////////////////////////////////

  &__row-selection

    :deep(.ui-table-top-row-selection__inner)
      width: 100cqw

  ///////////////////////////////////////

  &--checkbox-disabled
    :deep(.ui-button-icon__inner),
    :deep(.ui-button-icon__ripple)
      cursor: not-allowed

  ///////////////////////////////////////

  &__header-tooltip
    display: block
    width: 100%
    text-align: right

    :deep(.ui-tooltip-wrapper__inner)
      display: inline-flex
      align-items: center
      gap: space-x(0.375)
      white-space: nowrap
      vertical-align: middle

      @include media('<laptop')
        white-space: wrap

  &__header-tooltip-icon
    flex-shrink: 0
    color: $warning-alt
    font-size: 16px
    line-height: 1

  ///////////////////////////////////////

  &__empty
    position: sticky
    left: 0
    width: 100cqw
    display: flex
    flex-direction: column
    align-items: center
    margin: 20vh 0 space-x(2)
    gap: space-x(1)

  ///////////////////////////////////////

  &__footer
    border-top: 1px solid $border-color
    display: flex
    align-items: center
    justify-content: space-between
    padding: ($layout-spacer-y * .25) ($layout-spacer-x * .5)
    flex-shrink: 0

    &__count
      font-size: 85%

    &__per-page
      display: flex
      align-items: center
      gap: space-x(1)
      flex-shrink: 0

      &__text
        font-weight: $font-weight-light
        font-size: 90%
        white-space: nowrap

  ///////////////////////////////

  &__integre-paie-icon
    color: $success
    background-color: mix-alpha($success, 0.1)
    padding: space-x(0.25)
    border-radius: $border-radius-sm

  ///////////////////////////////

  :deep(.satd-saisies-list-page__table-main.b-table.table)
    @include table-presets
    @include table-body-grow

    margin-bottom: 0
    height: 100%

    /// Header chrome, needed on both nodes:
    ///   - `thead`: outlives the `hide-header` state (see <ui-table-wrapper>,
    ///     `visibility` suppressing the element own painting)
    ///   - `th`: travels with the sticky pinning (see below)
    $header-background: $background-primary
    $header-rule: inset 0 -1px 0 $submenu-border-color, inset 0 1px 0 $submenu-border-color

    thead
      background-color: $header-background
      box-shadow: $header-rule

    @include table-header-small
      background-color: $header-background
      box-shadow: $header-rule

    #{ $self }__cumul-row__label
      font-weight: $font-weight-bold
      letter-spacing: 0.5px

    #{ $self }__selection
      width: $selection-column-width
      text-align: center

    #{ $self }__actions
      width: $actions-column-width

    #{ $self }__salarie
      max-width: 100%
      width: $salarie-column-width

    #{ $self }__type-saisie
      width: $type-saisie-column-width
      text-align: center

    #{ $self }__date-avis
      width: $date-avis-column-width
      text-align: right

    #{ $self }__montant-total,
    #{ $self }__montant-passe,
    #{ $self }__montant-periode,
    #{ $self }__solde-du
      width: $montant-column-width
      text-align: right

      &--disable
        color: $gray-light
        background-color: mix-alpha($dark, 3.175%)

    #{ $self }__solde-du
      &--badge
        font-size: 11.5px

    #{ $self }__integre-paie
      width: $integre-paie-column-width
      white-space: nowrap
      text-align: center

  ////////////////////////////////////

  /// Sticky/fixed blocks
  ///
  /// (note that "boostrap-vue-next" ":sticky-header" prop is used to make
  /// the header sticky via an additional inner scroll → we already have such
  /// a container, see "__table-scroll", hence the ad hoc override)

  &__table-main:deep()
    thead th
      position: sticky
      top: 0
      z-index: z-index($z-indexes, 'main', 'ceil')

    tfoot
      background-color: map-get($palette-layout, 'color-3')
      position: sticky
      bottom: 0
      z-index: z-index($z-indexes, 'main', 'ceil')

    tfoot tr.satd-saisies-list-page__cumul-row td
      gap: 0
      color: $white
      font-weight: $font-weight-normal
      overflow: visible
      text-align: right
      font-size: 14px
      transition: opacity 200ms ease

    tfoot tr.satd-saisies-list-page__cumul-row--loading td:not(:first-child)
      opacity: 0.5

  ////////////////////////////////////

  /// Animation
  ///

  &__filters-container
    animation: fade-in 150ms ease-in-out

  &__empty
    animation: fade-in 300ms ease-in-out

  /// Grace delay before showing the loading rows: a fast enough consumer
  /// unmounts them before they ever get visible (avoiding a flicker whenever
  /// the result set is systematically empty)
  &__placeholder
    opacity: 0
    animation: fade-in 150ms ease-in-out $placeholder-grace-delay forwards

</style>

<style lang="sass">
.satd-saisies-list-page__popover

  &.ui-popover.b-popover

    .popover-body
      min-width: 120px
      // @TODO : delete with warning popover generate virement file
      max-width: 320px

</style>
